import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Phone, Mail, CheckCircle2, ArrowLeft, Calendar, MessageSquare } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";

const HERO_BG = "https://images.unsplash.com/photo-1582719508461-905c673771fd?w=1920&q=85";

const TOPICS = [
    { id: "buying_land", label: "I want to buy Fiji land" },
    { id: "selling_land", label: "I want to sell my land" },
    { id: "investing", label: "I have capital to invest in Joint Ventures" },
    { id: "business_buy", label: "I want to acquire a Fiji business" },
    { id: "business_sell", label: "I want to sell my business" },
    { id: "other", label: "Something else — let's talk" },
];

const SERVICES = [
    "Land for sale",
    "Joint Venture opportunities",
    "Business buyout",
    "Capital partnership",
    "Real estate agent representation",
    "Property valuation",
];

export default function Consultation() {
    const navigate = useNavigate();
    const [submitted, setSubmitted] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [form, setForm] = useState({
        full_name: "",
        email: "",
        phone: "",
        topic: "buying_land",
        message: "",
        services_interest: [],
    });

    const toggleService = (svc) => {
        setForm((f) => ({
            ...f,
            services_interest: f.services_interest.includes(svc)
                ? f.services_interest.filter((s) => s !== svc)
                : [...f.services_interest, svc],
        }));
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!form.full_name || !form.email || !form.phone || !form.message) {
            toast.error("Name, email, phone, and message are required.");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/jv-leads", {
                ...form,
                lead_type: "consultation",
                source: "consultation_page",
            });
            setSubmitted(true);
            toast.success("Request sent. We'll be in touch within 1 business day.");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Failed to send");
        } finally {
            setSubmitting(false);
        }
    };

    if (submitted) {
        return (
            <div className="max-w-2xl mx-auto px-4 py-20 text-center" data-testid="consultation-thanks">
                <div className="h-20 w-20 rounded-full bg-fiji-green/15 mx-auto grid place-items-center mb-6">
                    <CheckCircle2 className="h-10 w-10 text-fiji-green" />
                </div>
                <h1 className="font-display text-4xl font-bold mb-3">You're on our calendar.</h1>
                <p className="font-app text-muted-foreground mb-8">
                    The Bula team will reach out within 1 business day to confirm your consultation. Expect a call from a Fiji number — usually +679.
                </p>
                <div className="flex justify-center gap-3 flex-wrap">
                    <Button asChild variant="outline"><Link to="/marketplace">Browse listings</Link></Button>
                    <Button asChild className="bg-ocean-green text-white"><Link to="/jv">Joint Venture Portal</Link></Button>
                </div>
            </div>
        );
    }

    return (
        <div data-testid="consultation-page">
            <section className="relative h-[44vh] flex items-end overflow-hidden">
                <img src={HERO_BG} alt="Fiji" className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-ocean-deep via-ocean-deep/70 to-transparent" />
                <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12 w-full">
                    <div className="text-xs uppercase tracking-[0.3em] text-coral font-app font-semibold mb-2">Talk to the Bula team</div>
                    <h1 className="font-display text-4xl sm:text-5xl font-bold text-white max-w-3xl leading-tight">
                        Free 30-minute consultation — figure out your best move in Fiji.
                    </h1>
                    <p className="text-white/85 font-app text-lg mt-3 max-w-2xl">
                        Buying land, selling a business, raising capital, or just exploring — book a call and we'll map your options across the marketplace.
                    </p>
                </div>
            </section>

            <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid lg:grid-cols-3 gap-8">
                <aside className="space-y-4">
                    <Card className="p-5">
                        <Calendar className="h-5 w-5 text-fiji-green mb-2" />
                        <div className="font-display text-lg font-semibold">30-minute call</div>
                        <p className="text-sm font-app text-muted-foreground mt-1">No pressure, no commitment. We'll listen first, then suggest options.</p>
                    </Card>
                    <Card className="p-5">
                        <MessageSquare className="h-5 w-5 text-coral mb-2" />
                        <div className="font-display text-lg font-semibold">Platform-routed</div>
                        <p className="text-sm font-app text-muted-foreground mt-1">All messaging goes through Bula — we vet who you talk to.</p>
                    </Card>
                    <Card className="p-5 bg-secondary/40">
                        <div className="text-xs uppercase tracking-wider text-muted-foreground font-app">Or reach us directly</div>
                        <div className="flex items-center gap-2 font-app text-sm mt-2">
                            <Mail className="h-4 w-4 text-ocean dark:text-turquoise" /> hello@bularealestatefiji.com
                        </div>
                        <div className="flex items-center gap-2 font-app text-sm mt-1">
                            <Phone className="h-4 w-4 text-coral" /> +679 (call from Fiji)
                        </div>
                    </Card>
                </aside>

                <Card className="p-6 lg:col-span-2">
                    <form onSubmit={submit} data-testid="consultation-form" className="space-y-4">
                        <div className="grid sm:grid-cols-2 gap-4">
                            <div>
                                <Label>Full name *</Label>
                                <Input data-testid="cons-name" value={form.full_name} onChange={(e) => setForm((f) => ({ ...f, full_name: e.target.value }))} className="mt-1.5" />
                            </div>
                            <div>
                                <Label>Phone *</Label>
                                <Input data-testid="cons-phone" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} placeholder="+679 …" className="mt-1.5" />
                            </div>
                            <div className="sm:col-span-2">
                                <Label>Email *</Label>
                                <Input data-testid="cons-email" type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} className="mt-1.5" />
                            </div>
                        </div>
                        <div>
                            <Label>What's your main focus?</Label>
                            <Select value={form.topic} onValueChange={(v) => setForm((f) => ({ ...f, topic: v }))}>
                                <SelectTrigger data-testid="cons-topic" className="mt-1.5"><SelectValue /></SelectTrigger>
                                <SelectContent>{TOPICS.map((t) => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="mb-2 block">What services interest you? <span className="text-muted-foreground font-normal text-xs">(optional, multi-select)</span></Label>
                            <div className="grid sm:grid-cols-2 gap-2" data-testid="cons-services">
                                {SERVICES.map((svc) => {
                                    const selected = form.services_interest.includes(svc);
                                    return (
                                        <label key={svc} className={`flex items-center gap-2 p-2.5 rounded-md border cursor-pointer transition ${selected ? "border-ocean bg-ocean/5 dark:border-turquoise dark:bg-turquoise/5" : "border-border hover:border-muted-foreground/40"}`}>
                                            <Checkbox checked={selected} onCheckedChange={() => toggleService(svc)} />
                                            <span className="font-app text-sm">{svc}</span>
                                        </label>
                                    );
                                })}
                            </div>
                        </div>
                        <div>
                            <Label>Tell us more *</Label>
                            <Textarea data-testid="cons-message" value={form.message} onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))} rows={4} placeholder="e.g. 'Looking at 5–10 acre tourism-zoned land on Viti Levu, FJ$200k–500k budget, Joint Venture-open.'" className="mt-1.5" />
                        </div>
                        <Button type="submit" disabled={submitting} data-testid="cons-submit-btn" className="w-full bg-ocean-green text-white h-11">
                            {submitting ? "Sending…" : "Request consultation"}
                        </Button>
                        <p className="text-xs text-muted-foreground font-app text-center">By submitting, you agree to be contacted by the Bula Real Estate Fiji team about your enquiry.</p>
                    </form>
                </Card>
            </section>
        </div>
    );
}
